import { Button, Group, Text } from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconTrash,
  IconUser,
  IconVideo,
} from "@tabler/icons-react";
import { useState } from "react";
import { deleteEvent } from "api";
import { useAssistantWidgetStatus } from "stores/useAssistantWidgetStatus";
import { notify } from "utils/common";
import { formatTimeRange, resolveWidgetTimezone } from "./calendarWidgetTime";
import css from "./MicChatDrawer.module.scss";

const CalendarEventDeleteWidget = ({ deletion, statusKey }) => {
  const tz = resolveWidgetTimezone(deletion.timezone);
  const persistedStatus = useAssistantWidgetStatus(
    (state) => state.statusByKey[statusKey],
  );
  const markStatus = useAssistantWidgetStatus((state) => state.markStatus);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(persistedStatus === "deleted");
  const [isDismissed, setIsDismissed] = useState(
    persistedStatus === "dismissed",
  );

  const attendees = Array.isArray(deletion.attendees) ? deletion.attendees : [];

  const handleDelete = async () => {
    if (isDeleting || isDeleted) return;

    setIsDeleting(true);
    try {
      await deleteEvent(deletion.eventId);
      setIsDeleted(true);
      markStatus(statusKey, "deleted");
      notify("Event cancelled successfully!", "success");
    } catch (error) {
      notify(error.message || "Failed to cancel event", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const statusCopy = isDeleted
    ? "Event cancelled"
    : isDismissed
      ? "Kept on your calendar"
      : "Confirm cancellation";

  return (
    <div className={css.calendarDraftCard}>
      <Group position="apart" align="flex-start" spacing="sm" noWrap>
        <div className={css.calendarDraftTitleGroup}>
          <span
            className={`${css.calendarDraftStatusIcon} ${
              isDeleted
                ? css.calendarDraftStatusReady
                : css.calendarDraftStatusNeedsWork
            }`}
          >
            {isDeleted ? (
              <IconCheck size={14} />
            ) : (
              <IconAlertTriangle size={14} />
            )}
          </span>
          <div>
            <Text weight={800} size="sm" color="#253047">
              Cancel event
            </Text>
            <Text size="xs" color="#667085">
              {statusCopy}
            </Text>
          </div>
        </div>
        {isDeleted && <span className={css.calendarDraftPill}>Cancelled</span>}
      </Group>

      <div className={css.calendarDraftFields}>
        <div>
          <Text className={css.calendarDraftLabel}>Title</Text>
          <Text size="sm" color="#253047" weight={600}>
            {deletion.title || "Untitled meeting"}
          </Text>
        </div>

        <div>
          <Text className={css.calendarDraftLabel}>When</Text>
          <Text size="sm" color="#253047">
            {formatTimeRange(
              {
                startDateTimeIso: deletion.startDateTimeIso,
                endDateTimeIso: deletion.endDateTimeIso,
              },
              tz,
            )}
          </Text>
        </div>

        {attendees.length > 0 && (
          <div>
            <Text className={css.calendarDraftLabel}>Attendees</Text>
            {attendees.map((attendee) => (
              <div
                key={attendee.email || attendee.displayName}
                className={css.calendarDraftWarningRow}
              >
                <IconUser size={13} />
                <span>{attendee.displayName || attendee.email}</span>
              </div>
            ))}
          </div>
        )}

        {deletion.callLink && (
          <div className={css.calendarDraftWarningRow}>
            <IconVideo size={13} />
            <span>Includes a video call link</span>
          </div>
        )}
      </div>

      {!isDeleted && !isDismissed && (
        <Text size="xs" color="#667085" className={css.calendarDraftNotice}>
          This permanently removes the event from your calendar and cancels any
          paired video call.
        </Text>
      )}

      <Group position="right" spacing="xs" className={css.calendarDraftFooter}>
        <Button
          compact
          variant="default"
          disabled={isDeleting || isDeleted || isDismissed}
          onClick={() => {
            setIsDismissed(true);
            markStatus(statusKey, "dismissed");
          }}
        >
          Keep event
        </Button>
        <Button
          compact
          color="red"
          leftIcon={<IconTrash size={14} />}
          disabled={isDeleted || isDismissed}
          loading={isDeleting}
          onClick={handleDelete}
        >
          Cancel event
        </Button>
      </Group>
    </div>
  );
};

export default CalendarEventDeleteWidget;
